import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ADDON_UPLOAD_KIND_LABELS, ARTIFACT_TYPES, ARTIFACT_TYPE_LABELS, type ArtifactType, pageTitle } from '@thottbot/shared';
import { isRedirect, useArtifact } from '../api/client';
import { Badge } from '../components/Badge';
import { ArtifactFields } from '../features/artifact/ArtifactFields';
import { ScreenshotGallery } from '../features/social/ScreenshotGallery';
import { Comments } from '../features/social/Comments';
import { VoteArrows } from '../features/social/VoteArrows';
import { NotFound } from './NotFound';
import { relativeTime } from '../util/time';

function isType(t: string | undefined): t is ArtifactType {
  return !!t && (ARTIFACT_TYPES as readonly string[]).includes(t);
}

export function Artifact() {
  const { type, slug } = useParams();
  const navigate = useNavigate();
  const valid = isType(type) && !!slug;
  const result = useArtifact(valid ? type : 'quest', slug ?? '');
  const [showRevisions, setShowRevisions] = useState(false);

  useEffect(() => {
    if (result.data && isRedirect(result.data)) navigate(result.data.redirectTo, { replace: true });
  }, [result.data, navigate]);

  if (!valid) return <NotFound />;
  if (result.isPending) return <div className="page muted">Loading...</div>;
  if (result.isError) {
    if (result.error.status === 410) return <NotFound message="This entry was removed." />;
    if (result.error.status === 404)
      return <NotFound message="No such entry. Maybe you can add it?" />;
    return <div className="page error">Could not load this entry.</div>;
  }
  if (isRedirect(result.data)) return null;

  const { artifact, revisions, screenshots, comments, myVote, zone, questGivers, sources, contributors = [] } = result.data;
  const label = ARTIFACT_TYPE_LABELS[artifact.type].singular;
  const icon =
    artifact.type === 'item' || artifact.type === 'ability'
      ? (artifact.data.iconUrl as string | undefined)
      : undefined;
  const portrait =
    artifact.type === 'npc' ? (artifact.data.imageUrl as string | undefined) : undefined;
  const observed = artifact.data.observed as { uploads: number; build: string } | undefined;

  return (
    <div className="page">
      <title>{pageTitle(artifact.name, label)}</title>
      <h1>
        {icon && <img className="icon" src={icon} alt="" />}
        {portrait && (
          <img className="portrait" src={portrait} alt="" data-testid="npc-portrait" />
        )}
        {artifact.name}
        <Badge status={artifact.dataStatus} build={artifact.sourceBuild} />
      </h1>
      <p className="muted actions">
        <span>{label}</span>
        <span>·</span>
        <VoteArrows
          key={`${artifact.id}-${myVote}`}
          targetType="artifact"
          targetId={artifact.id}
          score={artifact.score}
          myVote={myVote}
          compact
        />
        <span>·</span>
        <button
          type="button"
          className="vote-arrow"
          style={{ color: 'var(--link)' }}
          onClick={() => setShowRevisions(!showRevisions)}
        >
          Revision {artifact.revisionNo}
        </button>
        <span>·</span>
        <Link to={`/${artifact.type}/${artifact.slug}/edit`}>Edit</Link>
      </p>
      {showRevisions && (
        <div className="table-scroll">
          <table className="grid" data-testid="revisions">
            <thead>
              <tr>
                <th>Revision</th>
                <th>By</th>
                <th>Note</th>
                <th>Status</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {revisions.map((r) => (
                <tr key={r.revisionNo}>
                  <td>{r.revisionNo}</td>
                  <td>{r.authorBattletag ?? 'seed'}</td>
                  <td>{r.note}</td>
                  <td>
                    <Badge status={r.dataStatus} build={r.sourceBuild} />
                  </td>
                  <td>{relativeTime(r.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ArtifactFields
        type={artifact.type}
        name={artifact.name}
        data={artifact.data}
        zone={zone}
        questGivers={questGivers}
        sources={sources}
      />

      <section className="section">
        <h2>Screenshots</h2>
        <ScreenshotGallery
          artifactId={artifact.id}
          artifactType={artifact.type}
          artifactSlug={artifact.slug}
          screenshots={screenshots}
        />
      </section>

      <section className="section">
        <h2>Comments</h2>
        <Comments
          artifactId={artifact.id}
          artifactType={artifact.type}
          artifactSlug={artifact.slug}
          comments={comments}
        />
      </section>
      {observed && observed.uploads > 0 && (
        <p className="muted observed-footer" data-testid="observed-footer">
          Built from {observed.uploads} {observed.uploads === 1 ? 'upload' : 'uploads'} (build {observed.build})
          {contributors.length > 0 && (
            <>
              :{' '}
              {contributors.map((c, i) => (
                <span key={`${c.battletag}-${c.createdAt}`} data-testid="contributor" data-kind={c.kind}>
                  {i > 0 && ', '}
                  {ADDON_UPLOAD_KIND_LABELS[c.kind].toLowerCase()} upload by {c.battletag} {relativeTime(c.createdAt)}
                </span>
              ))}
            </>
          )}
          . <Link to="/addon">Add yours.</Link>
        </p>
      )}
    </div>
  );
}
